/**
 * Janome JEF.
 *
 * Header (little endian): u32 stitch data offset (0x74 + 8 × colours), u32 flags (0x14),
 * 14-byte date "YYYYMMDDHHMMSS", 2 zero bytes, u32 colour count, u32 point count, u32 hoop code,
 * u32 × 4 design extents from centre, 4 × (u32 × 4) distances to the edges of standard hoops
 * (−1 when the design does not fit), u32 Janome palette index per colour, u32 0x0D per colour.
 * Stitches: 2 bytes (signed dx, −dy). Control: 0x80 0x01 dx dy colour change,
 * 0x80 0x02 dx dy jump, 0x80 0x10 end. Max displacement ±127.
 *
 * JEF has no explicit trim command: Janome machines trim on jumps according to the machine's
 * own setting (commonly jumps longer than ~3 mm). Trims in the sequence are therefore not written.
 * Colours are stored as indices into the 78-colour Janome palette (nearest match).
 *
 * Reference: EmbroidePy/pyembroidery (MIT) JefWriter/JefReader; independent implementation.
 */
import { t } from '../i18n/i18n';
import { CMD } from '../engine/stitch/sequence';
import { ByteReader, ByteWriter, FormatError } from './bytes';
import { type FormatLimits, type MachinePattern, type MachineStitch, extents } from './machine';
import { JEF_THREADS, hexToRgb, nearestIndex } from './threads';

export const JEF_LIMITS: FormatLimits = { maxStitch: 127, maxJump: 127, maxCoord: Number.MAX_SAFE_INTEGER };

export const JEF_HOOP = { H110X110: 0, H50X50: 1, H140X200: 2, H126X110: 3, H200X200: 4 } as const;

export function jefHoopCode(width: number, height: number): number {
  if (width < 500 && height < 500) return JEF_HOOP.H50X50;
  if (width < 1260 && height < 1100) return JEF_HOOP.H126X110;
  if (width < 1400 && height < 2000) return JEF_HOOP.H140X200;
  if (width < 2000 && height < 2000) return JEF_HOOP.H200X200;
  return JEF_HOOP.H110X110;
}

/** Nearest Janome colours; consecutive different threads never share an index. */
export function jefPalette(hexes: string[]): number[] {
  const out: number[] = [];
  let lastIndex = -1;
  let lastHex: string | null = null;
  for (const hex of hexes) {
    const rgb = hexToRgb(hex);
    let idx = nearestIndex(rgb, JEF_THREADS);
    if (idx === lastIndex && lastHex !== null && lastHex.toLowerCase() !== hex.toLowerCase()) {
      const without = JEF_THREADS.slice();
      without[idx] = null;
      idx = nearestIndex(rgb, without);
    }
    out.push(idx);
    lastIndex = idx;
    lastHex = hex;
  }
  return out;
}

function d8(v: number): number {
  if (!Number.isInteger(v) || v < -127 || v > 127) throw new FormatError(t('JEF displacement outside ±127: {v}', { v }));
  return v & 0xff;
}

function formatDate(d: Date): string {
  const p = (n: number, w = 2) => String(n).padStart(w, '0');
  return `${p(d.getFullYear(), 4)}${p(d.getMonth() + 1)}${p(d.getDate())}${p(d.getHours())}${p(d.getMinutes())}${p(d.getSeconds())}`;
}

export interface JefOptions {
  date?: Date;
}

export function writeJef(p: MachinePattern, opts: JefOptions = {}): Uint8Array {
  if (p.stitches.some((s) => s.cmd === CMD.STOP)) throw new FormatError(t('JEF export does not support stops without a colour change.'));
  const hasStitch = p.stitches.some((s) => s.cmd === CMD.STITCH);
  const blocks = hasStitch ? p.stitches.filter((s) => s.cmd === CMD.COLOR_CHANGE).length + 1 : 0;
  const hexes = Array.from({ length: blocks }, (_, i) => p.threads[i]?.hex ?? '#000000');
  const palette = jefPalette(hexes);
  const colorCount = palette.length;

  let pointCount = 1; // END
  for (const s of p.stitches) {
    if (s.cmd === CMD.STITCH) pointCount += 1;
    else if (s.cmd === CMD.JUMP || s.cmd === CMD.COLOR_CHANGE) pointCount += 2;
  }
  const e = extents(p.stitches);
  const width = e.maxX - e.minX;
  const height = e.maxY - e.minY;
  const hw = Math.round(width / 2);
  const hh = Math.round(height / 2);

  const w = new ByteWriter();
  w.u32le(0x74 + colorCount * 8);
  w.u32le(0x14);
  w.ascii(formatDate(opts.date ?? new Date()));
  w.u8(0).u8(0);
  w.u32le(colorCount);
  w.u32le(pointCount);
  w.u32le(jefHoopCode(width, height));
  w.u32le(hw).u32le(hh).u32le(hw).u32le(hh);
  const edge = (x: number, y: number) => {
    if (Math.min(x, y) >= 0) w.i32le(x).i32le(y).i32le(x).i32le(y);
    else w.i32le(-1).i32le(-1).i32le(-1).i32le(-1);
  };
  edge(550 - hw, 550 - hh); // 110 × 110
  edge(250 - hw, 250 - hh); // 50 × 50
  edge(700 - hw, 1000 - hh); // 140 × 200
  edge(700 - hw, 1000 - hh); // custom
  for (const idx of palette) w.u32le(idx);
  for (let i = 0; i < colorCount; i++) w.u32le(0x0d);

  let cx = 0, cy = 0;
  for (const s of p.stitches) {
    const dx = s.x - cx;
    const dy = s.y - cy;
    switch (s.cmd) {
      case CMD.STITCH:
        w.u8(d8(dx)).u8(d8(-dy));
        break;
      case CMD.JUMP:
        w.u8(0x80).u8(0x02).u8(d8(dx)).u8(d8(-dy));
        break;
      case CMD.COLOR_CHANGE:
        w.u8(0x80).u8(0x01).u8(d8(dx)).u8(d8(-dy));
        break;
      default:
        break;
    }
    if (s.cmd === CMD.STITCH || s.cmd === CMD.JUMP || s.cmd === CMD.COLOR_CHANGE) {
      cx = s.x;
      cy = s.y;
    }
  }
  w.u8(0x80).u8(0x10);
  return w.toBytes();
}

export interface JefFile {
  stitchOffset: number;
  date: string;
  colorCount: number;
  pointCount: number;
  hoop: number;
  extents: [number, number, number, number];
  palette: number[];
  stitches: MachineStitch[];
}

export function readJef(bytes: Uint8Array): JefFile {
  const r = new ByteReader(bytes);
  const stitchOffset = r.u32le();
  r.u32le();
  const date = r.ascii(14);
  r.skip(2);
  const colorCount = r.u32le();
  const pointCount = r.u32le();
  const hoop = r.u32le();
  const ext: [number, number, number, number] = [r.i32le(), r.i32le(), r.i32le(), r.i32le()];
  if (stitchOffset !== 0x74 + colorCount * 8 || stitchOffset > bytes.length) throw new FormatError(t('Invalid stitch offset in the JEF header.'));
  r.seek(0x74);
  const palette: number[] = [];
  for (let i = 0; i < colorCount; i++) palette.push(r.i32le());
  r.seek(stitchOffset);
  const stitches: MachineStitch[] = [];
  let x = 0, y = 0;
  let ended = false;
  while (r.remaining >= 2) {
    const b0 = r.u8();
    const b1 = r.u8();
    if (b0 !== 0x80) {
      x += b0 > 127 ? b0 - 256 : b0;
      y -= b1 > 127 ? b1 - 256 : b1;
      stitches.push({ x, y, cmd: CMD.STITCH });
      continue;
    }
    if (b1 === 0x10) {
      ended = true;
      break;
    }
    const dx = r.i8();
    const dy = -r.i8();
    x += dx;
    y += dy;
    if (b1 === 0x02) stitches.push({ x, y, cmd: CMD.JUMP });
    else if (b1 === 0x01) stitches.push({ x, y, cmd: CMD.COLOR_CHANGE });
    else throw new FormatError(t('Unknown JEF control code 0x{code}.', { code: b1.toString(16) }));
  }
  if (!ended) throw new FormatError(t('The JEF file is missing the end code (0x80 0x10).'));
  stitches.push({ x, y, cmd: CMD.END });
  return { stitchOffset, date, colorCount, pointCount, hoop, extents: ext, palette, stitches };
}
